import { defineStore } from 'pinia';

import { licenseService } from '~/services/license-service';
import type { RefundLicenseResponse } from '~/types/licenses/licenses';
import type { ContactType } from '~/types/person/contacts';

export const useLicenseStore = defineStore('licenses', () => {
    // State
    const isLoading = ref(false);
    const refundData = ref<RefundLicenseResponse | null>(null);
    const error = ref<string | null>(null);
    const { showSuccess, showError, showWarning } = useGlobalToast();

    // Actions
    const refundLicense = async (
        personId: number,
        licenseId: number,
        reason: string
    ): Promise<RefundLicenseResponse|void> => {
        try {
            refundData.value = null;
            isLoading.value = true;
            error.value = null;

            const { data } = await licenseService.refundLicense(personId, licenseId, { reason });
            
            refundData.value = data;

            const personStore = usePersonStore();
            await personStore.getPerson();

            const authStore = useAuthStore();
            await authStore.getMe();
        } catch (err: any) {
            error.value = err.message || 'Failed to refund license';
            // Don't show toast here - let the UI handle the error display
            throw err; // Re-throw the error so calling components can handle it
        } finally {
            isLoading.value = false;
        }
    };

    const updateLicenseRenewal = async (
        personId: number,
        licenseId: number,
        renew: boolean
    ): Promise<boolean> => {
        try {
            isLoading.value = true;
            error.value = null;

            await licenseService.updateLicense(personId, licenseId, { renew });

            return true;
        } catch (err: any) {
            showError("Error", err.message || 'Failed to update license');
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    const assignLicense = async (
        personId: number,
        licenseId: number,
        assignedToId: number,
        contactType: ContactType
    ): Promise<boolean> => {
        try {
            isLoading.value = true;
            error.value = null;

            await licenseService.assignLicense(personId, licenseId, {
                id: Math.abs(assignedToId),
                type: contactType,
                name: ''
            });
            
            // Refresh person data after successful assignment
            const personStore = usePersonStore();
            await personStore.getPerson();
            
            // Refresh auth data
            const authStore = useAuthStore();
            await authStore.getMe();
            
            return true;
        } catch (err: any) {
            error.value = err.message || 'Failed to assign license';
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    const unassignLicense = async (
        personId: number,
        licenseId: number
    ): Promise<boolean> => {
        try {
            isLoading.value = true;
            error.value = null;

            await licenseService.unassignLicense(personId, licenseId);
            
            // Refresh person data after successful unassignment
            const personStore = usePersonStore();
            await personStore.getPerson();
            
            // Refresh auth data
            const authStore = useAuthStore();
            await authStore.getMe();
            
            return true;
        } catch (err: any) {
            error.value = err.message || 'Failed to unassign license';
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    const cancelScheduledChange = async (
        personId: number,
        licenseId: number
    ): Promise<boolean> => {
        try {
            isLoading.value = true;
            error.value = null;

            await licenseService.cancelScheduledChange(personId, licenseId);

            // Refresh person data so the licenses list drops the scheduled-change indicator.
            const personStore = usePersonStore();
            await personStore.getPerson();

            return true;
        } catch (err: any) {
            error.value = err.message || 'Failed to cancel scheduled change';
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const resetRefundData = () => {
        refundData.value = null;
    };

    return {
        // State
        isLoading: readonly(isLoading),
        refundData: readonly(refundData),
        error: readonly(error),
        
        // Actions
        refundLicense,
        updateLicenseRenewal,
        clearError,
        resetRefundData,
        assignLicense,
        unassignLicense,
        cancelScheduledChange
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLicenseStore, import.meta.hot))
  }